import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  BarChart3,
  Briefcase,
  Activity,
  CalendarClock,
  FileSearch,
  FileStack,
  HelpCircle,
  Inbox,
  KeyRound,
  Menu,
  MessageCircleQuestion,
  Search,
  Trophy,
  Users,
  X,
} from "lucide-react";
import { apiFetch } from "../../utils/api";

const BULK_SCAN_STATE_KEY = "recruitglance_bulk_scan_state";
const BULK_SCAN_STATE_TTL_MS = 30 * 60 * 1000;
const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || "recruitglance@gmail.com";

export const RECRUITER_MENU_LABELS = {
  dashboard: "Dashboard",
  jobs: "Jobs",
  candidates: "Candidates",
  candidate_processes: "Background Activity",
  bulk: "Bulk Scan",
  scan_history: "Scan History",
  rankings: "Rankings",
  single: "Single CV Scan",
  candidate_search: "AI Talent Search",
  inbox_to_shortlist: "Inbox to Shortlist",
  api: "API Access",
  help: "Help",
  faq: "FAQ",
};

export default function RecruiterSidebar({
  activeItem = "dashboard",
  onToolSelect,
  user,
  scansLeft,
  summaryStats,
  title = "Recruiter Panel",
}) {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [backgroundRunning, setBackgroundRunning] = useState(false);
  const [backgroundCount, setBackgroundCount] = useState(0);
  const [bulkScanRunning, setBulkScanRunning] = useState(false);

  const isActive = user?.is_active === true;
  const isPro = user?.plan === "pro";
  const isPilot = user?.plan === "pilot";
  const isEnterprise = user?.plan === "enterprise";
  const hasInboxToShortlistAccess = isPro || isPilot || isEnterprise;
  const hasPaidPlan = user?.plan === "basic" || user?.plan === "pro" || user?.plan === "pilot" || user?.plan === "enterprise";
  const hasApiAccess = isPro || isPilot || isEnterprise;
  const isFree = user?.plan === "free";
  const quotaExceeded = scansLeft !== null && scansLeft !== undefined && scansLeft <= 0;

  useEffect(() => {
    if (!user || !hasPaidPlan) {
      setBackgroundRunning(false);
      setBackgroundCount(0);
      return undefined;
    }

    let cancelled = false;

    const fetchBackgroundActivity = async () => {
      try {
        const res = await apiFetch("/candidates/search-index-process-groups?limit=5");
        const data = await res.json();

        if (!res.ok || cancelled) return;

        const activeGroups = data?.active || [];
        const activeCount = activeGroups.reduce(
          (total, group) => total + (Number(group.processing) || 0) + (Number(group.pending) || 0),
          0
        );

        setBackgroundRunning(activeGroups.length > 0 && activeCount > 0);
        setBackgroundCount(activeCount);
      } catch {
        if (!cancelled) {
          setBackgroundRunning(false);
          setBackgroundCount(0);
        }
      }
    };

    fetchBackgroundActivity();
    const intervalId = window.setInterval(fetchBackgroundActivity, 10000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [user, hasPaidPlan]);

  useEffect(() => {
    if (!user || !hasPaidPlan) {
      setBulkScanRunning(false);
      return undefined;
    }

    const refreshBulkScanStatus = () => {
      try {
        const savedState = JSON.parse(localStorage.getItem(BULK_SCAN_STATE_KEY) || "null");
        const startedAt = savedState?.started_at ? new Date(savedState.started_at).getTime() : null;
        const activeStatus = savedState?.status === "scanning" || savedState?.status === "enriching";
        const expired = !startedAt || Number.isNaN(startedAt) || Date.now() - startedAt > BULK_SCAN_STATE_TTL_MS;

        if (savedState && expired) {
          localStorage.removeItem(BULK_SCAN_STATE_KEY);
          setBulkScanRunning(false);
          return;
        }

        setBulkScanRunning(Boolean(activeStatus && !expired));
      } catch {
        setBulkScanRunning(false);
      }
    };

    refreshBulkScanStatus();
    window.addEventListener("storage", refreshBulkScanStatus);
    window.addEventListener("recruitglance:bulk-scan-status", refreshBulkScanStatus);
    const intervalId = window.setInterval(refreshBulkScanStatus, 5000);

    return () => {
      window.removeEventListener("storage", refreshBulkScanStatus);
      window.removeEventListener("recruitglance:bulk-scan-status", refreshBulkScanStatus);
      window.clearInterval(intervalId);
    };
  }, [user, hasPaidPlan]);

  const formatPilotDate = (dateValue) => {
    if (!dateValue) return "Starts on first login";

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "Not scheduled";

    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const renderPilotAccessInfo = () => {
    if (!isPilot) return null;

    return (
      <div className="mt-3 rounded-md border border-emerald-300/15 bg-white/5 p-2.5 text-[11px] text-emerald-50">
        <p className="font-semibold uppercase tracking-wide text-emerald-200/80">
          Pilot Access
        </p>
        <div className="mt-2 space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-emerald-100/60">Started</span>
            <span className="font-semibold text-white">
              {formatPilotDate(user?.pilot_started_at)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-emerald-100/60">Ends</span>
            <span className="font-semibold text-white">
              {formatPilotDate(user?.pilot_expires_at)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const menuSections = [
    {
      title: "Overview",
      items: [
        { action: "dashboard", icon: BarChart3, path: "/dashboard_recruiter" },
      ],
    },
    {
      title: "Hiring Setup",
      items: [
        { action: "jobs", icon: Briefcase, path: "/jobs" },
        { action: "candidates", icon: Users, path: "/candidates", locked: !hasPaidPlan },
        { action: "candidate_processes", icon: Activity, path: "/candidate-processes", locked: !hasPaidPlan, nested: true },
      ],
    },
    {
      title: "Screening",
      items: [
        { action: "bulk", icon: FileStack, path: "/bulk-scan", locked: !hasPaidPlan || quotaExceeded },
        { action: "scan_history", icon: CalendarClock, path: "/scan-history", locked: !hasPaidPlan, nested: true },
        isFree ? { action: "single", icon: FileSearch, locked: !isActive || quotaExceeded } : null,
        { action: "rankings", icon: Trophy, path: "/rankings", locked: !hasPaidPlan },
      ].filter(Boolean),
    },
    {
      title: "AI Tools",
      items: [
        { action: "inbox_to_shortlist", icon: Inbox, path: "/inbox-to-shortlist", locked: !hasInboxToShortlistAccess || quotaExceeded },
        { action: "candidate_search", icon: Search, locked: !isPro && !isPilot && !isEnterprise },
      ],
    },
    {
      title: "Account & Platform",
      items: [
        { action: "api", icon: KeyRound, locked: !hasApiAccess },
        { action: "help", icon: HelpCircle, path: "/help" },
        { action: "faq", icon: MessageCircleQuestion, path: "/faq", nested: true },
      ],
    },
  ];

  const activeLabel = RECRUITER_MENU_LABELS[activeItem] || title;
  const visibleSummaryStats = Array.isArray(summaryStats) ? summaryStats.filter(Boolean) : [];

  const handleClick = (item) => {
    if (item.locked) {
      toast(
        !isActive
          ? "Your account is inactive."
          : quotaExceeded
          ? "You can preview this page, but scanning is blocked until your quota refreshes."
          : "Preview this feature and upgrade when you are ready to use it."
      );
    }

    if (item.path) {
      if (item.action === "dashboard" && onToolSelect) {
        onToolSelect("dashboard");
      }

      setMobileOpen(false);
      navigate(item.path);
      return;
    }

    if (onToolSelect) {
      setMobileOpen(false);
      onToolSelect(item.action);
      return;
    }

    setMobileOpen(false);
    navigate(`/dashboard_recruiter?tool=${item.action}`);
  };

  const renderMenuItem = (item) => {
    const Icon = item.icon;
    const isSelected = activeItem === item.action;

    return (
      <button
        key={item.action}
        onClick={() => handleClick(item)}
        className={`flex min-h-[32px] items-center gap-2 rounded-md px-2.5 py-1 text-[13px] font-medium transition
          ${isSelected ? "bg-white text-emerald-950 shadow-sm" : "text-emerald-100/80 hover:bg-white/10 hover:text-white"}
          ${item.locked ? "opacity-65" : ""}
          ${item.nested ? "ml-5 border-l border-emerald-200/15 pl-3 text-[12px]" : ""}`}
      >
        <Icon size={16} className={isSelected ? "text-emerald-700" : "text-emerald-200/70"} />
        <span className="min-w-0 flex-1 text-left">{RECRUITER_MENU_LABELS[item.action]}</span>
        {((item.action === "candidate_processes" && backgroundRunning) || (item.action === "bulk" && bulkScanRunning)) && (
          <span
            className="relative flex h-3 w-3 flex-shrink-0"
            title={
              item.action === "bulk"
                ? "Bulk scan running"
                : `${backgroundCount || "Some"} background task${backgroundCount === 1 ? "" : "s"} running`
            }
            aria-label={item.action === "bulk" ? "Bulk scan running" : "Background activity running"}
          >
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-300 opacity-75" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-300" />
          </span>
        )}
        {item.locked && (
          <span className="rounded-full border border-emerald-200/20 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-100/70">
            Locked
          </span>
        )}
      </button>
    );
  };

  const renderMenuSections = () =>
    menuSections.map((section) => (
      <div key={section.title} className="grid gap-0.5">
        <p className="px-2.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-200/45">
          {section.title}
        </p>
        {section.items.map(renderMenuItem)}
      </div>
    ));

  const renderSummaryStats = () => {
    if (!visibleSummaryStats.length) return null;

    return (
      <div className="rounded-md border border-emerald-300/15 bg-white/5 p-2">
        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-200/70">
          Workspace Snapshot
        </p>
        <div className="grid grid-cols-2 gap-1.5">
          {visibleSummaryStats.map((item) => (
            <div key={item.label} className="rounded-md bg-white/5 p-1.5">
              <p className="truncate text-[10px] font-medium text-emerald-100/55">{item.label}</p>
              <p className="mt-0.5 truncate text-sm font-semibold tabular-nums text-white">{item.value}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <nav className="lg:h-full">
      <div className="lg:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen((open) => !open)}
          className="flex min-h-[42px] w-full items-center justify-between rounded-lg border border-emerald-950 bg-[#03140f] px-3 text-sm font-semibold text-white"
          aria-expanded={mobileOpen}
        >
          <span className="flex min-w-0 items-center gap-2">
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            <span className="truncate">{activeLabel}</span>
          </span>
          <span className="text-xs font-medium text-emerald-200/70">Menu</span>
        </button>

        {mobileOpen && (
          <div className="mt-3 grid gap-3 rounded-lg border border-emerald-950 bg-[#03140f] p-2 shadow-sm">
            {renderMenuSections()}
            {renderSummaryStats()}
            {renderPilotAccessInfo()}
          </div>
        )}
      </div>

      <div className="hidden h-full min-h-0 flex-col gap-2 overflow-hidden lg:flex">
        <div className="flex-shrink-0 border-b border-white/10 pb-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-200/70">
            {title}
          </p>
          <p className="mt-1 text-[13px] font-semibold text-white">
            RecruitGlance Workspace
          </p>
        </div>

        <div className="recruiter-sidebar-scroll grid min-h-0 flex-1 content-start gap-2 overflow-y-auto pr-1">
          {renderMenuSections()}
        </div>
        <div className="flex-shrink-0 space-y-2 pt-2">
          {renderSummaryStats()}
          <p className="px-1 text-[11px] leading-5 text-emerald-100/55">
            Need help? Write to{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=RecruitGlance%20support%20request`}
              className="font-semibold text-emerald-100 underline decoration-emerald-300/40 underline-offset-2 hover:text-white"
            >
              {SUPPORT_EMAIL}
            </a>
          </p>
          {renderPilotAccessInfo()}
        </div>
      </div>
    </nav>
  );
}
